import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api/client.js";

/**
 * FIX-012 closure correction (2026-09-07, canonical copy directive):
 * the backend's `InvalidOrExpiredTokenError` message is shared across
 * email-verification/password-reset/invitation flows ("This <kind> link
 * is invalid or has expired.") — never itself rewritten, since
 * `VerifyEmailPage`'s own error text is out of scope here. This page
 * substitutes the exact canonical user-facing text for THAT one error
 * shape specifically (covering invalid, expired, AND already-used
 * tokens alike — a used token IS "no longer valid"), leaving every
 * other possible error (e.g. a weak new password) shown as-is.
 */
const TOKEN_NO_LONGER_VALID_MESSAGE = "This password reset link is no longer valid. Request a new password reset link.";

function describeResetError(err: unknown): string {
  if (err instanceof ApiError && err.message.includes("invalid or has expired")) {
    return TOKEN_NO_LONGER_VALID_MESSAGE;
  }
  return err instanceof ApiError ? err.message : "Reset failed — please try again.";
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (token === null || token.length === 0) {
      setError("This link is missing its reset token.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", null, { token, newPassword });
      setDone(true);
    } catch (err) {
      setError(describeResetError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <h2>Password reset</h2>
          <div className="success-banner">Your password has been reset. All existing sessions have been signed out for security.</div>
          <button className="primary" style={{ width: "100%" }} onClick={() => navigate("/login", { replace: true })}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h2>Reset your password</h2>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className="field">
            <label htmlFor="newPassword">New password</label>
            <input id="newPassword" type="password" required minLength={12} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input id="confirmPassword" type="password" required minLength={12} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={submitting} style={{ width: "100%" }}>
            {submitting ? "Resetting…" : "Reset password"}
          </button>
        </form>
        <div className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
